import React, { useEffect, useState } from "react";
import { Box, Stack, TextField, Checkbox, FormControlLabel, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { IExpression, IPackage } from "../../../models/configuration/plugin/IConfiguration";
import { IExpressionEvaluation } from '../../../models/IExpressionValidation';
import ExpressionEditor from "../../shared/config/ExpressionEditor";
import { useHostDetails } from "../../../hooks/useHostDetails";

interface PackageDetailsProps {
    selectedPackage: IPackage | null,
    handleOnEnabledChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
    handleOnEnableForTpoUserChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
    handleOnPackageIdChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
    handleOnCaptionChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
    handleOnToolTipTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
    handleOnAutoSelectChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

const autoSelectExpressionState: IExpression = {
    ExpressionName: "Auto Select",
    ExpressionString: "",
    ExpressionMessage: "",
    Enabled: false
}

const PackageDetails: React.FC<PackageDetailsProps> =
    ({
        selectedPackage,
        handleOnEnabledChange,
        handleOnEnableForTpoUserChange,
        handleOnCaptionChange,
        handleOnPackageIdChange,
        handleOnToolTipTextChange,
        handleOnAutoSelectChange
    }) => {
        const [autoSelect, setAutoSelect] = useState<boolean>(false);
        const [autoSelectExpressionOpen, setAutoSelectExpressionOpen] = useState(false);
        const [autoSelectExpression, setAutoSelectExpression] = useState<IExpression>(autoSelectExpressionState);
        const { host } = useHostDetails();

        useEffect(() => {
            if (selectedPackage) {
                setAutoSelectExpression((prevState) => ({
                    ...prevState,
                    ExpressionString: selectedPackage.AutoSelectWhen ?? ""
                }));
                setAutoSelect(!!selectedPackage.AutoSelectWhen);
            }
        }, [selectedPackage]);

        const handleAutoSelectOpen = () => setAutoSelectExpressionOpen(true);
        const handleAutoSelectClose = () => setAutoSelectExpressionOpen(false);

        // Validate IExpression
        const handleValidateExpression = (validationResult: IExpressionEvaluation) => {
            if (validationResult && !validationResult.error) setAutoSelect(true);
        };

        const updateExpressions = (expression: IExpression): string | null => {
            if (expression) {
                handleOnAutoSelectChange({ target: { value: expression.ExpressionString } } as React.ChangeEvent<HTMLInputElement>);
                setAutoSelectExpression(expression);
                setAutoSelectExpressionOpen(false);
            }
            return null;
        };

        return (
            <Stack spacing={2}>
                {/* First row: text fields */}
                <Stack direction="row" spacing={2} sx={{ pt: 2}}>
                     <TextField
                        className="caption-field"
                        onChange={handleOnPackageIdChange}
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={selectedPackage?.Id ?? ""}
                        label="Package Id"
                    />
                    <TextField
                        className="caption-field"
                        onChange={handleOnCaptionChange}
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={selectedPackage?.Caption ?? ""}
                        label="Caption"
                    />
                </Stack>
                {/* Second row: checkboxes */}
                <Stack direction="row" spacing={2}>
                    <TextField
                        className="tooltip-field"
                        onChange={handleOnToolTipTextChange}
                        variant="outlined"
                        size="small"
                        value={selectedPackage?.ToolTipText ?? ""}
                        label="ToolTip Text"
                        sx={{ flex: 1 }}
                    />
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center',}}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    onChange={handleOnEnabledChange}
                                    checked={selectedPackage?.Enabled ?? false}
                                />
                            }
                            label="Enabled"
                        />
                        
                        {host.isTPOEnabled && <FormControlLabel
                            control={
                                <Checkbox
                                    onChange={handleOnEnableForTpoUserChange}
                                    checked={selectedPackage?.EnableForTpoUser ?? false}
                                />
                            }
                            label="Enable TPO"
                        />}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={autoSelect ?? false}
                                        onChange={handleAutoSelectOpen}
                                    />
                                }
                                label={<>Auto-Select</>}
                            />
                            {autoSelect && (
                                <IconButton
                                    color="primary"
                                    onClick={handleAutoSelectOpen}
                                    size="small"
                                    title="Edit Auto-Select Rule"
                                    sx={{ ml: -1 }}
                                >
                                    <EditIcon fontSize="small"/>
                                </IconButton>
                            )}
                        </Box>
                        
                    </Box>
                </Stack>

                <ExpressionEditor
                    editorTitle="Setup Auto Select Rule"
                    expression={autoSelectExpression}
                    onValidate={handleValidateExpression}
                    onSave={updateExpressions}
                    renderMode='dialog'
                    editorMode='add'
                    open={autoSelectExpressionOpen}
                    onCloseDialog={handleAutoSelectClose}
                />
            </Stack>
        );
    }

export default PackageDetails;